import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import get from 'lodash/get';

import { Author } from 'components/molecules';
import { AuthorPropTypes } from 'components/molecules/author/Author';
import { defaultLocale } from 'i18n/config';
import { useAppContext } from 'contexts/app-context';
import { eventTrack, CONTENT_SHARE_CLICK } from 'utils/google-tag-manager';

import * as Styled from './Byline.styled';
import { ICONS } from '../../../constants/icons';

/**
 * Dayjs doesn't support all language/country formats, using the language alone
 * should suffice for rendering month names.
 * @param {string} locale The current app locale.
 */
const formatLocaleForDayjs = locale => `${locale.split('-').shift()}`;

/**
 * Formats a dateString from Contentful into a MONTH/YEAR format.
 * @param {string} locale The current locale.
 * @param {string} publishDate The `Publish Date` string from Contentful.
 * @returns {string} A formatted date string.
 */
const getFormattedDate = (locale, publishDate) => {
  const pubDate = dayjs(publishDate);

  return pubDate.isValid()
    ? pubDate.locale(locale).format('MMMM DD YYYY')
    : null;
};

/**
 * Renders a byline for the detail page.
 * @param {Object} author An author object:
 * @param {string} author.authorName The author's name.
 * @param {string} author.authorRole The author's role.
 * @param {string} publishDate The `Publish Date` string from Contentful.
 * @param {Boolean} isDarkTheme boolean value to determine the logo/font colors
 * @returns {ReactElement}
 */
const Byline = ({ author = null, publishDate, isDarkTheme }) => {
  const [appState] = useAppContext();
  const { locale } = appState;
  const authorName = get(author, 'authorName');
  const authorRole = get(author, 'authorRole');
  const defaultDayjsLocale = formatLocaleForDayjs(defaultLocale.id);
  const dayjsLocale = formatLocaleForDayjs(locale);
  const [pubDate, setPubDate] = useState(null);
  const dayjsPromise = useMemo(() => import(`dayjs/locale/${dayjsLocale}.js`), [
    dayjsLocale,
  ]);

  useEffect(() => {
    let isSubscribed = true;

    // Import localized date data
    dayjsPromise
      .then(() => {
        if (isSubscribed) {
          setPubDate(getFormattedDate(dayjsLocale, publishDate));
        }
      })
      .catch(() => {
        // Defaults to en-US on error
        if (isSubscribed) {
          setPubDate(getFormattedDate(defaultDayjsLocale, publishDate));
        }
      });

    // Prevents promise resolver from firing on unmounted component
    return () => {
      isSubscribed = false;
    };
  }, []);

  const onSocialClick = useCallback(event => {
    eventTrack(CONTENT_SHARE_CLICK, {
      event,
      authorName,
      publishDate,
    });
  }, []);

  return (
    <Styled.Container>
      <Styled.LogoContainer>
        <Styled.Logo name={ICONS.SPOTIFY_LOGO} isDarkTheme={isDarkTheme} />
        <Styled.Byline className="byline" isDarkTheme={isDarkTheme}>
          {authorName && <Author name={authorName} role={authorRole} />}
          {publishDate && (
            <Styled.PublishDate
              className="publishDate"
              isDarkTheme={isDarkTheme}
            >
              {pubDate}
            </Styled.PublishDate>
          )}
        </Styled.Byline>
      </Styled.LogoContainer>
      <Styled.ShareContainer>
        <p>Share This</p>
        <Styled.Social onClick={onSocialClick} isDarkTheme={isDarkTheme} />
      </Styled.ShareContainer>
    </Styled.Container>
  );
};

Byline.propTypes = {
  /**
   * Author object: {name, role}
   */
  author: PropTypes.shape(AuthorPropTypes),
  /**
   * The `Publish Date` string from Contentful.
   */
  publishDate: PropTypes.string,
};

export default Byline;
